import type { Effect, Reducer, Subscription } from '@umijs/max';
import type { SingleGoodCategoryType } from './data';
import {
  addGoodCategory,
  editGoodCategory,
  getGoodCategoryAll,
  removeGoodCategory,
} from './service';

export type GoodCategoryState = {
  code: number;
  type: string;
  message: string;
  items: SingleGoodCategoryType[];
  treeData: SingleGoodCategoryType[];
};

type GoodCategoryModelType = {
  namespace: string;
  state: GoodCategoryState;
  reducers: {
    GoodCategoryReducer: Reducer<GoodCategoryState>;
  };
  effects: {
    fetchGoodCategoryAllEffect: Effect;
    refreshDataEffect: Effect;
    addGoodCategoryEffect: Effect;
    editGoodCategoryEffect: Effect;
    removeGoodCategoryEffect: Effect;
  };
  subscriptions: {
    setup: Subscription;
  };
};

// 将扁平数据转换为树形结构
const buildTree = (data: SingleGoodCategoryType[]): SingleGoodCategoryType[] => {
  if (!data || data.length === 0) return [];

  const map = new Map<number, SingleGoodCategoryType>();
  const tree: SingleGoodCategoryType[] = [];

  // 先将所有节点放入 map
  data.forEach((item) => {
    map.set(item.id, { ...item, children: [] });
  });

  // 构建树形结构
  data.forEach((item) => {
    const node = map.get(item.id);
    if (node) {
      if (item.pid === 0) {
        // 根节点
        tree.push(node);
      } else {
        // 子节点
        const parent = map.get(item.pid);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(node);
        } else {
          // 如果找不到父节点，作为根节点处理
          tree.push(node);
        }
      }
    }
  });

  return tree;
};

// 通用操作处理函数
const createEffect = (serviceCall: any) =>
  function* (
    { payload: action, callback }: any,
    { call, put }: any,
  ): Generator<any, void, any> {
    try {
      const res: any = yield call(
        serviceCall,
        action.values || { id: action.id },
      );

      if (res && res.code === 0) {
        yield put({
          type: 'refreshDataEffect',
          payload: { ...action },
        });
      }

      callback?.(res);
    } catch (e) {}
  };

const GoodCategoryModel: GoodCategoryModelType = {
  namespace: 'goodCategory',
  state: {
    code: 0,
    type: '',
    message: 'success',
    items: [],
    treeData: [],
  },

  reducers: {
    GoodCategoryReducer(state, action) {
      const payload = action.payload || {};
      const data = payload.data;
      const items = Array.isArray(data) ? data : [];
      return {
        ...state,
        ...payload,
        items: items,
        treeData: buildTree(items),
      } as GoodCategoryState;
    },
  },

  effects: {
    *fetchGoodCategoryAllEffect(
      { payload }: any,
      { put, call }: any,
    ): Generator<any, void, any> {
      try {
        const filter = payload?.filter || {};
        const res: any = yield call(getGoodCategoryAll, filter);
        yield put({
          type: 'GoodCategoryReducer',
          payload: res,
        });
      } catch (e) {
        console.error('Fetch data failed:', e);
      }
    },

    // 通用刷新数据的 effect
    *refreshDataEffect(
      { payload: action }: any,
      { put }: any,
    ): Generator<any, void, any> {
      yield put({
        type: 'fetchGoodCategoryAllEffect',
        payload: {
          filter: { ...action.filter },
        },
      });
    },

    // 简化的 effects
    addGoodCategoryEffect: createEffect(addGoodCategory),
    editGoodCategoryEffect: createEffect(editGoodCategory),
    removeGoodCategoryEffect: createEffect(removeGoodCategory),
  },

  subscriptions: {
    setup() {},
  },
};

export default GoodCategoryModel;
